import { Store, MapPin, Phone, Facebook } from "lucide-react";
import { Link } from "react-router-dom";
import { Img } from "@/components/site/Img";
import { track } from "@/lib/track";

const redDoorPhotos = [
  {
    src: "/images/red-door-sitting-lions.jpg",
    title: "Sitting Lion Pair",
    alt: "Pair of concrete sitting lion statues on display at Red Door Antiques in LaGrange, GA",
  },
  {
    src: "/images/red-door-crowned-lions.jpg",
    title: "Crowned Lion Pair",
    alt: "Pair of concrete crowned lion statues on display at Red Door Antiques in LaGrange, GA",
    to: "/piece/crowned-lion",
  },
  {
    src: "/images/red-door-pineapple-charcoal.jpg",
    title: "Pineapple Fountain in Charcoal",
    alt: "Two-tier charcoal pineapple fountain on display at Red Door Antiques in LaGrange, GA",
    to: "/piece/pineapple-charcoal",
  },
  {
    src: "/images/red-door-cherub-birdbath.jpg",
    title: "Cherub Bird Bath",
    alt: "Concrete bird bath with a sitting cherub on the rim on display at Red Door Antiques in LaGrange, GA",
  },
  {
    src: "/images/red-door-baby-elephant.jpg",
    title: "Baby Elephant Statue",
    price: "$65",
    alt: "Concrete baby elephant statue with its trunk raised, natural gray, on display at Red Door Antiques in LaGrange, GA",
    to: "/piece/baby-elephant",
  },
  {
    src: "/images/red-door-buddha.jpg",
    title: "Buddha with Offering Bowl",
    price: "$85",
    alt: "Concrete sitting Buddha with offering bowl on display at Red Door Antiques in LaGrange, GA",
    to: "/piece/buddha-bowl",
  },
];

const RedDoorPhotoStrip = () => (
  <div data-testid="red-door-photo-strip" className="mt-7 md:mt-8 pt-6 border-t border-[#D5CEBD]">
    <div className="flex flex-wrap items-baseline justify-between gap-x-6 gap-y-1 mb-4">
      <div className="text-[10px] tracking-[0.28em] uppercase text-[#7A2E22] font-medium">
        Seen at Red Door
      </div>
      <p className="text-xs text-[#6B665C]">
        Recently photographed in the shop. Pieces sell quickly, so call Red Door to confirm one is still there.
      </p>
    </div>
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 md:gap-4">
      {redDoorPhotos.map((p) => {
        const slug = p.src.replace("/images/red-door-", "").replace(".jpg", "");
        const body = (
          <>
            <div className="aspect-[4/5] overflow-hidden bg-[#D5CEBD]">
              <Img
                src={p.src}
                alt={p.alt}
                loading="lazy"
                sizes="(min-width: 1024px) 15vw, (min-width: 640px) 33vw, 50vw"
                className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-500"
              />
            </div>
            <figcaption className="mt-2 text-[#1F1E1C] text-sm leading-snug">
              {p.title}
              {p.price && <span className="text-[#7A6547]"> &middot; {p.price}</span>}
              {p.to && <span className="block text-[11px] tracking-[0.12em] uppercase text-[#7A2E22] mt-0.5">View details</span>}
            </figcaption>
          </>
        );
        return (
          <figure key={p.src} data-testid={`red-door-photo-${slug}`} className="group">
            {p.to ? (
              <Link to={p.to} onClick={() => track("product_view", `${p.title} (Red Door strip)`)} className="block">
                {body}
              </Link>
            ) : (
              body
            )}
          </figure>
        );
      })}
    </div>
  </div>
);

export default function RedDoorStrip() {
  return (
    <section
      data-testid="red-door-strip"
      className="bg-[#F3EDDF] border-b border-[#D5CEBD] border-l-4 border-l-[#7A2E22]"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 lg:px-14 py-7 md:py-9">
      <div className="flex flex-col md:flex-row md:items-center gap-5 md:gap-8">
        <div className="flex items-start gap-4 flex-1">
          <div className="w-11 h-11 bg-[#7A2E22] flex items-center justify-center flex-shrink-0">
            <Store size={20} strokeWidth={1.5} className="text-[#F3EDDF]" />
          </div>
          <div>
            <div className="text-[10px] tracking-[0.28em] uppercase text-[#7A2E22] mb-1 font-medium">
              Shopping for finished pieces?
            </div>
            <div className="font-serif text-2xl md:text-[1.7rem] text-[#1F1E1C] leading-tight">
              Red Door Antiques has the biggest selection right now.
            </div>
            <p className="text-sm text-[#6B665C] mt-1.5 max-w-xl">
              Most of Chris&rsquo;s finished pieces are on display at Red Door
              Antiques in downtown LaGrange. Stop in and see them in person.
            </p>
            <div data-testid="red-door-strip-address" className="text-sm text-[#1F1E1C] mt-2 flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-1.5">
              <span className="flex items-center gap-1.5">
                <MapPin size={13} strokeWidth={1.5} className="text-[#7A2E22] flex-shrink-0" />
                5 East Lafayette Square, LaGrange, GA 30240
              </span>
              <span className="hidden sm:inline text-[#7A6547]">&middot;</span>
              <a
                href="tel:+17062987393"
                data-testid="red-door-strip-phone"
                className="hover:text-[#7A2E22] transition-colors whitespace-nowrap pl-[21px] sm:pl-0"
              >
                (706) 298-7393
              </a>
            </div>
          </div>
        </div>
        <div className="flex flex-col sm:flex-row gap-3 flex-shrink-0">
          <a
            href="https://www.google.com/maps/search/?api=1&query=Red+Door+Antiques+5+East+Lafayette+Square+LaGrange+GA+30240"
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => track("red_door_map_click", "Red Door strip")}
            data-testid="red-door-strip-directions"
            className="inline-flex items-center justify-center gap-2 px-5 py-3 bg-[#7A2E22] text-[#F9F6F0] text-[11px] tracking-[0.2em] uppercase hover:bg-[#1F1E1C] transition-colors"
          >
            <MapPin size={13} strokeWidth={1.5} />
            Get Directions
          </a>
          <a
            href="https://www.facebook.com/profile.php?id=100064790478742"
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => track("red_door_facebook_click", "Red Door strip")}
            data-testid="red-door-strip-facebook"
            className="inline-flex items-center justify-center gap-2 px-5 py-3 border border-[#7A2E22] text-[#7A2E22] text-[11px] tracking-[0.2em] uppercase hover:bg-[#7A2E22] hover:text-[#F9F6F0] transition-colors"
          >
            <Facebook size={13} strokeWidth={1.5} />
            Their Facebook
          </a>
        </div>
      </div>
      <RedDoorPhotoStrip />
      </div>
    </section>
  );
}
